// Campaign Approval Component
import React, { useState } from 'react';
import { useCampaign } from '../../context/CampaignContext';
import { useNotification } from '../../context/NotificationContext';
import { notificationModel } from '../../data/notificationModel';

const CampaignApproval = ({ campaign, onClose }) => {
  const { approveCampaign, rejectCampaign } = useCampaign();
  const { createNotification } = useNotification();
  const [loading, setLoading] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  const handleApprove = async () => {
    if (!window.confirm('ยืนยันการอนุมัติ Campaign นี้?')) return;

    setLoading(true);
    try {
      await approveCampaign(campaign.id);
      
      // Send notification to creator
      if (campaign.createdBy) {
        try {
          await createNotification(
            campaign.createdBy,
            notificationModel.types.CAMPAIGN_APPROVED,
            '✅ Campaign ถูกอนุมัติแล้ว',
            `Campaign "${campaign.name?.th || campaign.name?.en || 'Campaign'}" ถูกอนุมัติแล้ว`,
            { campaignId: campaign.id }
          );
        } catch (notifError) {
          console.error('Error sending notification:', notifError);
        }
      }

      alert('อนุมัติ Campaign สำเร็จ');
      // Close modal first
      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error('Error approving campaign:', error);
      alert('เกิดข้อผิดพลาด: ' + error.message);
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      alert('กรุณาระบุเหตุผลในการปฏิเสธ');
      return;
    }

    if (!window.confirm('ยืนยันการปฏิเสธ Campaign นี้?')) return;

    setLoading(true);
    try {
      await rejectCampaign(campaign.id, rejectionReason);
      
      // Send notification to creator
      if (campaign.createdBy) {
        try {
          await createNotification(
            campaign.createdBy,
            notificationModel.types.CAMPAIGN_REJECTED,
            '❌ Campaign ถูกปฏิเสธ',
            `Campaign "${campaign.name?.th || campaign.name?.en || 'Campaign'}" ถูกปฏิเสธ: ${rejectionReason}`,
            { campaignId: campaign.id, reason: rejectionReason }
          );
        } catch (notifError) {
          console.error('Error sending notification:', notifError);
        }
      }

      alert('ปฏิเสธ Campaign สำเร็จ');
      if (onClose) onClose();
    } catch (error) {
      console.error('Error rejecting campaign:', error);
      alert('เกิดข้อผิดพลาด: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg max-w-2xl">
      <h2 className="text-xl font-bold mb-4">อนุมัติ/ปฏิเสธ Campaign</h2>
      
      <div className="mb-4">
        <div className="text-sm text-gray-600 mb-2">ชื่อ Campaign:</div>
        <div className="font-medium">{campaign.name?.th || campaign.name?.en || 'ไม่มีชื่อ'}</div>
      </div>

      <div className="mb-4">
        <div className="text-sm text-gray-600 mb-2">ประเภท:</div>
        <div>{campaign.type}</div>
      </div>

      {/* Preview Banner */}
      {campaign.banner?.desktop?.th?.imageUrl && (
        <div className="mb-4">
          <div className="text-sm text-gray-600 mb-2">Banner Preview:</div>
          <img
            src={campaign.banner.desktop.th.imageUrl}
            alt="Banner"
            className="w-full h-32 object-cover rounded-lg border"
            onError={(e) => {
              e.target.src = 'https://via.placeholder.com/800x200?text=Banner+Preview';
            }}
          />
        </div>
      )}

      {/* Rejection Reason */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          เหตุผลในการปฏิเสธ (ถ้าต้องการปฏิเสธ):
        </label>
        <textarea
          value={rejectionReason}
          onChange={(e) => setRejectionReason(e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
          rows="3"
          placeholder="ระบุเหตุผล..."
        />
      </div>

      {/* Actions */}
      <div className="flex justify-end space-x-4">
        <button
          onClick={onClose}
          className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
        >
          ยกเลิก
        </button>
        <button
          onClick={handleReject}
          disabled={loading || !rejectionReason.trim()}
          className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50"
        >
          {loading ? 'กำลังดำเนินการ...' : 'ปฏิเสธ'}
        </button>
        <button
          onClick={handleApprove}
          disabled={loading}
          className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
        >
          {loading ? 'กำลังดำเนินการ...' : 'อนุมัติ'}
        </button>
      </div>
    </div>
  );
};

export default CampaignApproval;

